import { ColorEnum } from '@/constants/color'
import ModifierRow from '@/features/openTabs/components/AccountItemCard/parts/ModifierRow'
import IconButton from '@/features/ui/buttons/components/IconButton'
import DeleteIcon from '@/features/ui/icons/components/DeleteIcon'
import MinusIcon from '@/features/ui/icons/components/MinusIcon'
import MoreIcon from '@/features/ui/icons/components/MoreIcon'
import NoteIcon from '@/features/ui/icons/components/NoteIcon'
import PlusIcon from '@/features/ui/icons/components/PlusIcon'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface AccountItemView {
    id: string
    name: string
    quantity: number
    canRemove?: boolean
    totalPrice: string
    additions?: {
        id: string
        label: string
        totalPrice?: string
    }[]
    removals?: string[]
    note?: string
}

interface AccountItemCardProps {
    item: AccountItemView
    disabled?: boolean
    onEdit?: (itemId: string) => void
    onChangeQuantity?: (itemId: string, change: -1 | 1) => void
    onRemove?: (itemId: string) => void
    minimumQuantity?: number
    testID?: string
}

const AccountItemCard: FC<AccountItemCardProps> = ({
    item,
    disabled,
    onEdit,
    onChangeQuantity,
    onRemove,
    minimumQuantity = 0,
    testID,
}) => {
    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet, {
        layout: layout === 'compact' ? 'compact' : undefined,
    })
    const hasModifiers = !!item.additions?.length || !!item.removals?.length
    const [expanded, setExpanded] = useState(false)
    const editable = !!onEdit || !!onChangeQuantity || !!onRemove

    return (
        <View style={styles.container} testID={testID}>
            <Pressable
                disabled={!editable || disabled}
                accessibilityRole={editable ? 'button' : undefined}
                accessibilityLabel={
                    editable ? `${t('openTabs.cart.editItem')}: ${item.name}` : undefined
                }
                accessibilityState={editable ? { expanded, disabled } : undefined}
                onPress={() => setExpanded((value) => !value)}
                style={styles.itemContent}
                testID={testID ? `${testID}-pressable` : undefined}
            >
                <View style={styles.header}>
                    <Typography.Heading variant="h5" style={styles.name}>
                        {item.name}
                    </Typography.Heading>
                    <View style={styles.rightContent}>
                        <Typography.Heading variant="h5" style={styles.quantity}>
                            {t('openTabs.quantity.pieces', { count: item.quantity })}
                        </Typography.Heading>
                        <Typography.Heading variant="h5" style={styles.price}>
                            {item.totalPrice}
                        </Typography.Heading>
                    </View>
                </View>

                {hasModifiers && (
                    <View style={styles.modifiers}>
                        {item.additions?.map((addition) => (
                            <ModifierRow key={addition.id} type="addition" label={addition.label} />
                        ))}
                        {item.removals?.map((removal) => (
                            <ModifierRow key={removal} type="removal" label={removal} />
                        ))}
                    </View>
                )}
                {item.note && (
                    <View style={styles.note}>
                        <NoteIcon width={16} height={16} />
                        <Typography.Description size="small" style={styles.noteText}>
                            {item.note}
                        </Typography.Description>
                    </View>
                )}
            </Pressable>
            {editable && expanded && (
                <View style={styles.actions}>
                    <View style={styles.editActions}>
                        {onEdit && (
                            <IconButton
                                Icon={MoreIcon}
                                color="secondary"
                                type="outline"
                                size={44}
                                disabled={disabled}
                                accessibilityRole="button"
                                accessibilityLabel={t('openTabs.cart.editItem')}
                                onPress={() => onEdit(item.id)}
                            />
                        )}
                        {onRemove && item.canRemove && (
                            <IconButton
                                Icon={DeleteIcon}
                                color="secondary"
                                type="outline"
                                size={44}
                                disabled={disabled}
                                accessibilityRole="button"
                                accessibilityLabel={t('openTabs.cart.removeItem')}
                                onPress={() => onRemove(item.id)}
                            />
                        )}
                    </View>
                    <View style={styles.quantityActions}>
                        <IconButton
                            Icon={MinusIcon}
                            color="secondary"
                            type="outline"
                            size={44}
                            disabled={
                                disabled || !onChangeQuantity || item.quantity <= minimumQuantity
                            }
                            accessibilityRole="button"
                            accessibilityLabel={t('openTabs.cart.decreaseQuantity')}
                            onPress={() => onChangeQuantity?.(item.id, -1)}
                        />
                        <IconButton
                            Icon={PlusIcon}
                            color="secondary"
                            type="outline"
                            size={44}
                            disabled={disabled || !onChangeQuantity}
                            accessibilityRole="button"
                            accessibilityLabel={t('openTabs.cart.increaseQuantity')}
                            onPress={() => onChangeQuantity?.(item.id, 1)}
                        />
                    </View>
                </View>
            )}
        </View>
    )
}

const stylesheet = createStyleSheet({
    itemContent: { gap: 12 },
    actions: { flexDirection: 'row', justifyContent: 'space-between', gap: 8 },
    editActions: { flexDirection: 'row', gap: 8 },
    quantityActions: { flexDirection: 'row', gap: 8, marginLeft: 'auto' },
    container: {
        width: '100%',
        padding: 16,
        gap: 12,
        borderWidth: 1,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 8,
        backgroundColor: ColorEnum.WHITE,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 16,
    },
    name: {
        flex: 1,
        minWidth: 0,
        color: ColorEnum.GRAY_900,
    },
    rightContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        variants: {
            layout: {
                compact: {
                    alignItems: 'flex-start',
                    gap: 12,
                },
            },
        },
    },
    quantity: {
        color: ColorEnum.BRICK_600,
    },
    price: {
        width: 105,
        color: ColorEnum.GRAY_900,
        textAlign: 'right',
        variants: {
            layout: {
                compact: { width: 88 },
            },
        },
    },
    modifiers: {
        gap: 8,
    },
    note: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    noteText: {
        flex: 1,
        minWidth: 0,
        color: ColorEnum.GRAY_600,
    },
})

export default AccountItemCard
